import Fade from '@rio-cloud/rio-uikit/Fade';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGetOrdersQuery } from '#api/orderApi.slice.ts';
import { CancelDialog } from '#components/shared/CancelDialog.tsx';
import { Sidebar } from '#components/sidebar/Sidebar.tsx';
import type { Documents } from '#domain/Document.ts';
import type { StopDetails } from '#domain/Stop.ts';
import type { Tour, TourDetails } from '#domain/Tour.ts';
import { DocumentPollingProvider } from '#features/documents/hooks/documentPolling.context.tsx';
import { useDownloadTourDocumentArchive } from '#features/documents/hooks/useDownloadTourDocumentArchive.ts';
import { CreateTourDialog } from '#features/tourCreate/ui/CreateTourDialog.tsx';
import { useCancelTour } from '#features/tourDetails/hooks/useCancelTour.ts';
import { formatTourSidebarName } from '#features/tourDetails/model/formatTourSidebarName.ts';
import { TourDuplicateDialogs } from '#features/tourDuplicate/ui/TourDuplicateDialogs.tsx';

import { TourDetails as TourDetailsContent } from './TourDetails.tsx';
import { TourDetailsSidebarFooter } from './TourDetailsSidebarFooter.tsx';
import { TourStopDocumentList } from './TourStopDocumentList.tsx';
import { TourStopMaterialList } from './TourStopMaterialList.tsx';

interface TourDetailsSidebarProps {
    selectedTour: TourDetails;
    tourDocuments?: Documents;
    setDocumentDialogState: (isDialogOpen: boolean) => void;
    setSelectedTourId: (id: string | undefined) => void;
    handleSolveProblem: (tourId: string) => void;
    vehicleAssetId: string;
    vehicleName?: string;
}

export const TourDetailsSidebar = ({
    selectedTour,
    setDocumentDialogState,
    setSelectedTourId,
    handleSolveProblem,
    tourDocuments,
    vehicleAssetId,
    vehicleName,
}: TourDetailsSidebarProps) => {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedStop, setSelectedStop] = useState<StopDetails>();
    const [selectedDocumentStop, setSelectedDocumentStop] = useState<StopDetails>();

    const animationStyle = selectedStop || selectedDocumentStop ? 'page' : 'pageBack';
    const intl = useIntl();

    const {
        data: orders = [],
        isLoading: isLoadingOrders,
        isError: isOrderError,
    } = useGetOrdersQuery(['transport', 'service'], undefined);
    const selectedTourOrders = useMemo(
        () => orders.filter(order => order.tourId === selectedTour.id),
        [orders, selectedTour.id]
    );
    const editableTourOrders = useMemo(
        () => orders.filter(order => order.status === 'open' || order.tourId === selectedTour.id),
        [orders, selectedTour.id]
    );
    const selectedTourStops = selectedTour.stops;

    const tourName = formatTourSidebarName(selectedTour)?.replace(/^"|"$/g, '') ?? '';
    const cancelDialogContent = intl.formatMessage(
        {
            id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.tour.body',
        },
        { tourName }
    );

    const { cancelTour, isLoading } = useCancelTour();
    const { downloadTourDocumentArchive, isDownloading } = useDownloadTourDocumentArchive(selectedTour);

    const handleBack = () => {
        setSelectedStop(undefined);
        setSelectedDocumentStop(undefined);
    };

    const openEditDialog = () => setShowEditDialog(true);
    const closeEditDialog = () => setShowEditDialog(false);
    const openDuplicateDialog = () => setShowDuplicateDialog(true);

    const currentTourStop = selectedTour.stops.find(stop => stop.status !== 'finished');

    const handleSolveProblemClick = (tour: Tour) => {
        if (tour.problem?.type === 'declined-goods' && currentTourStop?.type === 'load') {
            openEditDialog();
        }

        void handleSolveProblem(tour.id);
    };

    const onClose = () => {
        setSelectedTourId(undefined);
        setSelectedStop(undefined);
        setShowDuplicateDialog(false);
        closeEditDialog();
    };

    const handleCancel = async (tour: Tour) => {
        return cancelTour(tour.id).then(success => {
            if (success) {
                Notification.success(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.cancel.single.success',
                    })
                );
                onClose();
            } else {
                Notification.error(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.cancel.single.failure',
                    })
                );
            }
        });
    };

    useEffect(() => {
        // When changing the selected tour, reset the selected stop
        if (selectedTourStops?.some((stop: StopDetails) => stop.id === selectedStop?.id)) {
            return;
        }
        setSelectedStop(undefined);
        setSelectedDocumentStop(undefined);
    }, [selectedStop?.id, selectedTourStops]);

    return (
        <Sidebar
            title={selectedTour?.searchName}
            isOpen={!!selectedTour}
            onClose={onClose}
            footer={
                <TourDetailsSidebarFooter
                    tour={selectedTour}
                    vehicleAssetId={selectedTour.status === 'in-transit' ? vehicleAssetId : undefined}
                    onEdit={openEditDialog}
                    onDuplicate={openDuplicateDialog}
                    onSolveProblem={handleSolveProblemClick}
                    onCancelTour={() => setShowDeleteDialog(true)}
                    onDownloadArchive={() => downloadTourDocumentArchive(selectedTour)}
                    isDownloadingArchive={isDownloading}
                />
            }
        >
            <div>
                <CancelDialog
                    show={showDeleteDialog}
                    isLoading={isLoading}
                    title={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.tour.title',
                    })}
                    content={cancelDialogContent}
                    cancelLabel={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.tour.keep',
                    })}
                    confirmLabel={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.tour.cancel',
                    })}
                    confirmIconName='trash'
                    onClickConfirm={async () => {
                        await handleCancel(selectedTour);
                        setShowDeleteDialog(false);
                    }}
                    onClickCancel={() => setShowDeleteDialog(false)}
                    cancelTrackingKey='ordercom.tours.cancel-dialog.cancel'
                    confirmTrackingKey='ordercom.tours.cancel-dialog.confirm'
                />
                {showDuplicateDialog && (
                    <TourDuplicateDialogs
                        isOpen={showDuplicateDialog}
                        selectedTour={selectedTour}
                        orders={selectedTourOrders}
                        isLoadingOrders={isLoadingOrders}
                        isOrderError={isOrderError}
                        onClose={() => setShowDuplicateDialog(false)}
                    />
                )}
                {showEditDialog && (
                    <CreateTourDialog
                        isOpen={showEditDialog}
                        close={closeEditDialog}
                        orders={selectedTourOrders}
                        availableOrders={editableTourOrders}
                        tourToEdit={selectedTour}
                    />
                )}
                <DocumentPollingProvider setIsDocumentDialogOpen={setDocumentDialogState}>
                    <Fade
                        initial={false}
                        exitBeforeEnter
                        animationStyle={animationStyle}
                        duration={0.25}
                        motionProps={{ transition: { duration: 0.25, type: 'spring' } }}
                    >
                        {!selectedStop && !selectedDocumentStop && (
                            <TourDetailsContent
                                key='tourDetails'
                                tour={selectedTour}
                                tourDocuments={tourDocuments}
                                selectedTourStops={selectedTourStops ?? []}
                                setSelectedStop={setSelectedStop}
                                setSelectedDocumentStop={setSelectedDocumentStop}
                                vehicleName={vehicleName}
                            />
                        )}
                        {selectedDocumentStop && (
                            <TourStopDocumentList
                                key='tourStopDocuments'
                                onBack={handleBack}
                                stopId={selectedDocumentStop.id}
                                tourId={selectedTour.id}
                            />
                        )}
                        {selectedStop && (
                            <TourStopMaterialList key='tourListDeviation' onBack={handleBack} stop={selectedStop} />
                        )}
                    </Fade>
                </DocumentPollingProvider>
            </div>
        </Sidebar>
    );
};
